/**
 * Update user tab: find the user, pick the field(s), see the current value,
 * enter the new one (a picklist where Vault has one). Submitting creates a
 * **proposal** to confirm; sensitive fields also need Administrator approval.
 */

import { useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconShieldLock, IconTrash } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  fieldLabel,
  getVeevaUser,
  listVeevaUsers,
  proposeChange,
  type ChangeOut,
  type PicklistsOut,
  type VeevaUserDetailOut,
  type VeevaUserRowOut,
} from "@/shared/api/userAdmin";
import { toastApiError } from "@/shared/ui/toast";
import { fmtValue, picklistFor } from "./picks";
import { ProposalCard } from "./ProposalCard";

interface Row {
  field: string | null;
  value: string;
}

export function UpdateUserForm({
  connectionId,
  lists,
}: {
  connectionId: string;
  lists: PicklistsOut | null;
}) {
  const getToken = useAccessToken();
  const [search, setSearch] = useState("");
  const [found, setFound] = useState<VeevaUserRowOut[]>([]);
  const [user, setUser] = useState<VeevaUserDetailOut | null>(null);
  const [rows, setRows] = useState<Row[]>([{ field: null, value: "" }]);
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState<ChangeOut | null>(null);
  const sensitive = new Set(lists?.sensitive_fields ?? []);
  const updatable = lists?.updatable_fields ?? [];

  const find = async () => {
    setBusy(true);
    try {
      const out = await listVeevaUsers(await getToken(), connectionId, search);
      setFound(out.items.slice(0, 20));
    } catch (err) {
      toastApiError("Could not search the users", err);
    } finally {
      setBusy(false);
    }
  };

  const pick = async (id: string | null) => {
    if (!id) return;
    try {
      setUser(await getVeevaUser(await getToken(), connectionId, id));
      setRows([{ field: null, value: "" }]);
    } catch (err) {
      toastApiError("Could not load the user", err);
    }
  };

  const submit = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const values = Object.fromEntries(
        rows.filter((r) => r.field).map((r) => [r.field as string, r.value]),
      );
      setProposal(
        await proposeChange(await getToken(), {
          connection_id: connectionId,
          kind: "update",
          target_user_id: user.id,
          values,
        }),
      );
    } catch (err) {
      toastApiError("Could not prepare the change", err);
    } finally {
      setBusy(false);
    }
  };

  if (proposal) {
    return (
      <Stack gap="sm">
        <ProposalCard initial={proposal} changeId={proposal.change_id} />
        <Button
          variant="default"
          onClick={() => {
            setProposal(null);
            setUser(null);
            setFound([]);
          }}
          style={{ alignSelf: "flex-start" }}
        >
          Change another user
        </Button>
      </Stack>
    );
  }

  const chosen = new Set(rows.map((r) => r.field).filter(Boolean));
  const ready = user && rows.some((r) => r.field) && rows.every((r) => !r.field || r.value !== "");
  return (
    <Stack gap="sm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void find();
        }}
      >
        <Group gap="xs" align="flex-end">
          <TextInput
            label="Find the user"
            placeholder="Name, username, email or id"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button type="submit" loading={busy} variant="default">
            Search
          </Button>
        </Group>
      </form>
      {found.length > 0 && (
        <Select
          label="User"
          placeholder="Pick a user"
          data={found.map((u) => ({
            value: u.id,
            label: `${u.name ?? u.username ?? u.id} · ${u.email ?? ""} (${u.id})`,
          }))}
          value={user?.id ?? null}
          onChange={(v) => void pick(v)}
          searchable
        />
      )}
      {user && (
        <Paper withBorder p="sm" radius="md">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Veeva user {user.id} · {fmtValue(user.fields.user_name__v)}
            </Text>
            {rows.map((row, i) => {
              const data = row.field ? picklistFor(lists, row.field) : null;
              const update = (patch: Partial<Row>) =>
                setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
              return (
                <Group key={i} align="flex-end" gap="xs" wrap="wrap">
                  <Select
                    label="Field"
                    data={updatable
                      .filter((f) => f === row.field || !chosen.has(f))
                      .map((f) => ({ value: f, label: fieldLabel(f) }))}
                    value={row.field}
                    onChange={(f) => update({ field: f, value: "" })}
                    w={200}
                  />
                  <Stack gap={2} w={160}>
                    <Text size="xs" c="dimmed">
                      Current
                    </Text>
                    <Text size="sm">{row.field ? fmtValue(user.fields[row.field]) : "—"}</Text>
                  </Stack>
                  {data ? (
                    <Select
                      label="New value"
                      data={data}
                      value={row.value || null}
                      onChange={(v) => update({ value: v ?? "" })}
                      searchable
                      w={260}
                    />
                  ) : (
                    <TextInput
                      label="New value"
                      value={row.value}
                      onChange={(e) => update({ value: e.currentTarget.value })}
                      w={260}
                    />
                  )}
                  {row.field && sensitive.has(row.field) && (
                    <Badge
                      color="orange"
                      variant="outline"
                      leftSection={<IconShieldLock size={12} />}
                    >
                      Needs admin approval
                    </Badge>
                  )}
                  {rows.length > 1 && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label="Remove field"
                      onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Group>
              );
            })}
            <Group>
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconPlus size={14} />}
                onClick={() => setRows((prev) => [...prev, { field: null, value: "" }])}
              >
                Another field
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}
      {!lists && (
        <Alert color="yellow" variant="light">
          Loading this vault's picklists…
        </Alert>
      )}
      <Button
        onClick={() => void submit()}
        loading={busy}
        disabled={!ready}
        style={{ alignSelf: "flex-start" }}
        data-testid="update-user-submit"
      >
        Review the change
      </Button>
    </Stack>
  );
}
